import React, { useState, useEffect, useRef } from 'react';

import { Box, CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { AssignmentOutlined } from '@material-ui/icons';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import CaseStatusCell from '../../components/cases/CaseStatusCell';
import BackButton from '../../components/common/BackButton';
import TypeChip from '../../components/common/TypeChip';
import CallBackObservationModal from '../../components/observation/CallBackObservationModal';
import MaterialsList from '../../components/observation/MaterialsList';
import TechnologistEvaluationModal from '../../components/observation/TechnologistEvaluationModal';
import VideoTracker from '../../components/video/VideoTracker';
import { MODALITY_OPTIONS_LABELS, observationColors, observationStatuses, TYPE_OPTIONS_ENUM } from '../../constants/observations';
import { useQualityObservationSocket } from '../../hooks/useWebSocketEvents';
import QualityObservationService from '../../services/quality-observation.service';

const useStyles = makeStyles((theme) => ({
  examInfoItem: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    justifyContent: 'flex-start',
  },
  examInfoLabel: {
    color: '#555',
    fontSize: 12,
    fontWeight: 'bold',
  },
  examInfoValue: {
    color: '#999',
    fontSize: 14,
    lineHeight: 1.37,
  },
  label: {
    display: 'flex',
    fontSize: 14,
    fontWeight: 500,
    gap: 8,
    justifyContent: 'flex-start',
  },
  mainSide: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    width: '100%',

    [theme.breakpoints.up('lg')]: {
      paddingRight: 30,
      width: 'calc(100% - 350px)',
    },
  },
  message: {
    color: '#455a64',
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 8,
  },
  pageTitle: {
    alignItems: 'center',
    display: 'flex',
    gap: 18,
    marginBottom: 16,
  },
  patientName: {
    color: '#263238',
    fontSize: 24,
    fontWeight: 500,
  },
  recordInfo: {
    display: 'flex',
    gap: 40,
    marginBottom: 16,
  },
  recordInfoItem: {
    color: '#455a64',
    fontSize: 14,
    fontWeight: 500,
  },
  root: {
    backgroundColor: 'white',
    fontFamily: 'Roboto !important',
    padding: '20px 70px',
  },
  seeLink: {
    '&:hover': {
      textDecoration: 'underline',
    },
    alignItems: 'center',
    color: '#4FC3F7',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 14,
    fontWeight: 500,
    gap: 6,
    textDecoration: 'none',
  },
  summaryContainer: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 16,
  },
  summaryText: {
    color: '#666',
    fontSize: 14,
    lineHeight: 1.5,
    textAlign: 'justify',
  },
  summaryTitle: {
    color: '#666',
    fontSize: 16,
    fontWeight: 'bold',
  },
  titleHR: {
    backgroundColor: '#EBEBEB',
    border: 'none',
    height: 1,
    margin: '16px 0',
    width: '100%',
  },
  videoPlayer: {
    borderRadius: 16,
    width: '100%',
  },
  videosSidebar: {
    backgroundColor: 'white',
    display: 'flex',
    flexDirection: 'column',
    padding: '30px 20px',

    [theme.breakpoints.up('lg')]: {
      boxShadow: '0px 0px 10px 0px #0000001A',
      height: 'calc(100vh - 125px)',
      overflow: 'auto',
      position: 'absolute',
      right: 0,
      top: -36,
      width: '350px',
      zIndex: 200,
    },
  },
  videosSidebarFixed: {
    [theme.breakpoints.up('lg')]: {
      height: '100vh',
      position: 'fixed',
      top: 0,
    },
  },
}));

const TechnologistEvaluationView = () => {
  const classes = useStyles();
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const sidebarRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [observation, setObservation] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [videoWatched, setVideoWatched] = useState(false);
  const [statModalOpen, setStatModalOpen] = useState(false);
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const watchedPercentageRef = useRef(0);

  const fetchObservation = async () => {
    try {
      setLoading(true);
      const data = await QualityObservationService.getDetailsById(id);
      setObservation(data);

      if (!data.openedByTechnologist) {
        await QualityObservationService.update(id, { openedByTechnologist: true, status: observationStatuses.in_progress.value });

        setObservation({
          ...data,
          openedByTechnologist: true,
          status: observationStatuses.in_progress.value,
        });
      }
    } catch (err) {
      console.error('Error fetching observation:', err);
      enqueueSnackbar('Failed to load observation details', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchObservation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const fetchMaterials = async () => {
      if (!id) return;

      try {
        setLoadingMaterials(true);
        const data = await QualityObservationService.getObservationMaterials(id);
        setMaterials(data || []);
      } catch (err) {
        console.error('Error fetching materials:', err);
        enqueueSnackbar('Failed to load materials', { variant: 'error' });
      } finally {
        setLoadingMaterials(false);
      }
    };

    fetchMaterials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const saveRoutineObservation = async () => {
    try {
      await QualityObservationService.complete(observation.id);

      enqueueSnackbar('Routine observation completed', { variant: 'success' });
      fetchObservation();
    } catch (error) {
      console.error('Error completing routine observation:', error);
      enqueueSnackbar('Failed to complete observation', { variant: 'error' });
    }
  };

  const handleVideoProgress = (percentage) => {
    watchedPercentageRef.current = percentage;
  };

  const handleThresholdReached = (percentage) => {
    watchedPercentageRef.current = percentage;
  };

  const saveVideoWatchedStatus = async () => {
    try {
      await QualityObservationService.update(observation.id, { videoWatched: true });
    } catch (error) {
      console.error('Error saving video watched status:', error);
    }
  };

  const handleVideoEnded = async () => {
    if (observation.status === observationStatuses.complete.value) {
      return;
    }

    const finalWatchedPercentage = watchedPercentageRef.current;

    if (finalWatchedPercentage < 95) {
      enqueueSnackbar(`Video should be watched completely to finish observation.`, { variant: 'error' });
      return;
    }

    setVideoWatched(true);

    await saveVideoWatchedStatus();

    if (observation.type === TYPE_OPTIONS_ENUM.ROUTINE) {
      if (observation.hasEvaluation) {
        setEvaluationModalOpen(true);
        return;
      }

      if (observation.transcriptionStatus === 'processing') {
        enqueueSnackbar('Quiz is being generated. wait a moment and try again.', { variant: 'info' });
        return;
      }

      await saveRoutineObservation();
      return;
    }

    setStatModalOpen(true);
  };

  const handleCloseCallbackModal = () => {
    setStatModalOpen(false);
    refreshObservationData();
  };

  const handleCloseEvaluationModal = (completed, shouldShowCallbackModal) => {
    setEvaluationModalOpen(false);

    if (shouldShowCallbackModal) {
      setStatModalOpen(true);
    }

    refreshObservationData();
  };

  const refreshObservationData = async () => {
    if (!id) return;

    try {
      const data = await QualityObservationService.getDetailsById(id);
      setObservation(data);
    } catch (err) {
      console.error('Error refreshing observation data:', err);
      enqueueSnackbar('Failed to refresh observation details', { variant: 'error' });
    }
  };

  const handleJumpToQuestions = () => {
    setEvaluationModalOpen(true);
  };

  useQualityObservationSocket({
    dependencies: [id],
    joinObservationRoom: true,
    observationId: id,
    onCreated: async (data) => {
      console.log('WebSocket onCreated received:', data);
      if (data.observationId === Number(id)) {
        enqueueSnackbar('Questions are ready!', { variant: 'success' });
        await fetchObservation();
      }
    },
    onFailed: async (data) => {
      console.log('WebSocket onFailed received:', data);
      if (data.observationId === Number(id)) {
        const errorMessage = data.errorReason || 'Failed to generate questions';
        enqueueSnackbar(errorMessage, { variant: 'error' });
        await fetchObservation();
      }
    },
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!observation) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <Typography variant="h6">Observation not found</Typography>
      </Box>
    );
  }

  const ExamInfoItem = ({ label, value }) => (
    <Box className={classes.examInfoItem}>
      <Typography variant="subtitle2" className={classes.examInfoLabel}>
        {label}:
      </Typography>
      <Typography variant="body2" className={classes.examInfoValue}>
        {value}
      </Typography>
    </Box>
  );

  return (
    <div className={classes.root}>
      <Box className={classes.mainSide}>
        <Box>
          <BackButton route="/worklist" />

          <div className={classes.pageTitle}>
            <Typography className={classes.patientName}>
              {observation.patientName} / {observation.examName}
            </Typography>

            <TypeChip type={observation.type} />

            <CaseStatusCell
              color={observationColors[observation.status]}
              blackFont={observation.status === observationStatuses.on_hold.value}
              status={observationStatuses[observation.status]?.label}
            />
          </div>

          <hr className={classes.titleHR} />

          <div className={classes.recordInfo}>
            <ExamInfoItem label="Exam name" value={observation.examName} />

            <ExamInfoItem label="Modality" value={MODALITY_OPTIONS_LABELS[observation.modality] || 'N/A'} />

            {/* <ExamInfoItem label="Body part" value={observation.bodyPart} /> */}
          </div>

          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} mt={4}>
            <Typography align="left" className={classes.message} style={{ marginBottom: 0 }}>
              Please watch this video and answer any follow-up questions that may appear after the video
            </Typography>

            {observation.videoWatched &&
              observation.hasEvaluation &&
              observation.type === TYPE_OPTIONS_ENUM.ROUTINE &&
              observation.status !== observationStatuses.complete.value && (
                <Typography className={classes.seeLink} onClick={handleJumpToQuestions}>
                  <AssignmentOutlined fontSize="small" />
                  Start Quiz
                </Typography>
              )}
          </Box>

          <CallBackObservationModal open={statModalOpen} onClose={handleCloseCallbackModal} observationId={observation.id} />

          <TechnologistEvaluationModal
            open={evaluationModalOpen}
            onClose={handleCloseEvaluationModal}
            observationId={observation.id}
            transcriptionStatus={observation.transcriptionStatus}
          />
        </Box>

        <VideoTracker
          src={observation.videoUrl}
          onVideoProgress={handleVideoProgress}
          onThresholdReached={handleThresholdReached}
          threshold={95}
          videoProps={{
            className: classes.videoPlayer,
            controls: true,
            onEnded: handleVideoEnded,
          }}>
          <track
            kind="captions"
            src={observation.vttSubtitles ? `data:text/vtt;base64,${btoa(observation.vttSubtitles)}` : ''}
            label="English"
            srcLang="en"
            default={!!observation.vttSubtitles}
          />
          Your browser does not support the video tag.
        </VideoTracker>

        {observation.summary && (
          <Box className={classes.summaryContainer}>
            <Typography className={classes.summaryTitle}>Summary</Typography>
            <Typography className={classes.summaryText}>{observation.summary}</Typography>
          </Box>
        )}

        {(videoWatched || observation.videoWatched) && observation.type === TYPE_OPTIONS_ENUM.ROUTINE && observation.transcriptionStatus === 'processing' && (
          <Box mt={2} p={2} bgcolor="#fff3e0" borderRadius={1} display="flex" alignItems="center" gridGap={16}>
            <CircularProgress size={20} />
            <Typography variant="body1">Quiz is being generated. Please wait...</Typography>
          </Box>
        )}

        {(videoWatched || observation.videoWatched) &&
          observation.type === TYPE_OPTIONS_ENUM.ROUTINE &&
          observation.transcriptionStatus !== 'processing' &&
          !observation.hasEvaluation && (
            <Box mt={2} p={2} bgcolor="#e8f5e9" borderRadius={1}>
              <Typography variant="body1">Video watched. No further action required.</Typography>
            </Box>
          )}
      </Box>

      {materials.length > 0 && (
        <Box className={`${classes.videosSidebar} ${isScrolled ? classes.videosSidebarFixed : ''}`} ref={sidebarRef}>
          <Box flexGrow={1}>
            <Typography style={{ alignItems: 'center', display: 'flex', fontSize: 20, fontWeight: 'bold', gap: 6, marginBottom: 16 }}>Materials</Typography>

            {loadingMaterials ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <MaterialsList materials={materials} selectedMaterials={materials} />
            )}
          </Box>
        </Box>
      )}
    </div>
  );
};

export default TechnologistEvaluationView;
